import axios from 'axios';
import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaSpinner, FaGift, FaSave, FaPercent, FaRupeeSign } from "react-icons/fa";
import LoadingScreen from '../../components/LoadingScreen';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

function EditOfferPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [offerData, setOfferData] = useState({ name: '', value: '', unit: '', minAmount: '', isActive: true, isWelcomeOffer: false });
    const [savingOffer, setSavingOffer] = useState(false);
    const [loading, setLoading] = useState(true);
    const token = localStorage.getItem('authToken');

    const handleApiError = (error) => {
        console.error('API Error:', error);
        alert(error.response?.data?.message || 'Something went wrong. Please try again.');
    };

    const getOfferData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/single-offer-data/${id}`);
            setOfferData({
                name: response.data.name || '',
                value: response.data.value !== undefined ? String(response.data.value) : '',
                unit: response.data.unit || '',
                minAmount: response.data.minAmount !== undefined ? String(response.data.minAmount) : '',
                isActive: response.data.isActive !== false,
                isWelcomeOffer: !!response.data.isWelcomeOffer,
            });
        } catch (error) {
            handleApiError(error);
            setTimeout(() => navigate('/admin/offers'), 2000);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setOfferData({
            ...offerData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const validateFields = () => {
        if (!offerData.name.trim()) {
            alert('Offer name is required');
            return false;
        }
        if (!String(offerData.value).trim() || isNaN(Number(offerData.value))) {
            alert('Offer value is required and must be a valid number');
            return false;
        }
        if (!offerData.unit.trim()) {
            alert('Offer unit is required');
            return false;
        }
        if (offerData.minAmount && isNaN(Number(offerData.minAmount))) {
            alert('Minimum amount must be a valid number');
            return false;
        }
        return true;
    };

    const handleUpdateOffer = async () => {
        if (!validateFields()) return;

        try {
            setSavingOffer(true);
            const response = await axios.patch(`${API_BASE_URL}/update-offer/${id}`, offerData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message);
            navigate('/admin/offers');
        } catch (error) {
            handleApiError(error);
        } finally {
            setSavingOffer(false);
        }
    };

    useEffect(() => {
        getOfferData();
    }, [id]);

    if (loading) {
        return <LoadingScreen />;
    }

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                {/* Header */}
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1">EDIT OFFER</h4>
                                <p className="text-muted p-0 m-0">Update offer information and details</p>
                            </div>
                            <button
                                type="button"
                                className="back-btn-pill"
                                onClick={() => navigate('/admin/offers')}
                                title="Back to Offers"
                            >
                                <FaArrowLeft />
                                Back to Offers
                            </button>
                        </div>
                    </div>
                </div>

                {/* Offer Form Card */}
                <div className="row mt-0">
                    <div className="col-12">
                        <div className="details-card-custom edit-offer-card-custom">
                            <div className="details-card-header">
                                <h5>
                                    <FaGift className="text-danger me-2" />
                                    Offer Information
                                </h5>
                            </div>
                            <div className="details-card-body p-4">
                                <div className="row">
                                    {/* Left Column - Fields */}
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label form-label-custom">Offer Name</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-custom"
                                                placeholder="Enter offer name"
                                                name="name"
                                                value={offerData.name}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label form-label-custom">Offer Value</label>
                                            <input
                                                type="number"
                                                min={0}
                                                className="form-control form-control-custom"
                                                placeholder="Enter offer value"
                                                name="value"
                                                value={offerData.value}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label form-label-custom">Unit</label>
                                            <select
                                                className="form-select form-control-custom"
                                                name="unit"
                                                value={offerData.unit}
                                                onChange={handleChange}
                                            >
                                                <option value="">Select Unit</option>
                                                <option value="Percent">Percent (%)</option>
                                                <option value="Rupees">Rupees (₹)</option>
                                            </select>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label form-label-custom">Minimum Amount</label>
                                            <input
                                                type="number"
                                                min={0}
                                                className="form-control form-control-custom"
                                                placeholder="Enter minimum order amount for offer"
                                                name="minAmount"
                                                value={offerData.minAmount}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="mb-3 form-check form-switch d-flex align-items-center">
                                            <input 
                                                type="checkbox" 
                                                className="form-check-input me-2" 
                                                id="isWelcomeOffer"
                                                name="isWelcomeOffer" 
                                                checked={offerData.isWelcomeOffer} 
                                                onChange={handleChange} 
                                                style={{ width: '38px', height: '20px', cursor: 'pointer' }}
                                            />
                                            <label className="form-check-label fw-semibold text-dark mb-0" htmlFor="isWelcomeOffer" style={{ cursor: 'pointer' }}>Is New User Welcome Offer</label>
                                        </div>
                                        
                                        <div className="mb-4 form-check form-switch d-flex align-items-center">
                                            <input 
                                                type="checkbox" 
                                                className="form-check-input me-2" 
                                                id="isActive"
                                                name="isActive" 
                                                checked={offerData.isActive} 
                                                onChange={handleChange} 
                                                style={{ width: '38px', height: '20px', cursor: 'pointer' }}
                                            />
                                            <label className="form-check-label fw-semibold text-dark mb-0" htmlFor="isActive" style={{ cursor: 'pointer' }}>Is Offer Active</label>
                                        </div>

                                        <button
                                            className="save-brand-btn w-100"
                                            disabled={savingOffer}
                                            onClick={handleUpdateOffer}
                                        >
                                            {savingOffer ? (
                                                <>
                                                    <FaSpinner className="spinner-border spinner-border-sm me-2" />
                                                    Updating Offer...
                                                </>
                                            ) : (
                                                <>
                                                    <FaSave className="me-2" />
                                                    Update Offer
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    {/* Right Column - Live Preview */}
                                    <div className="col-md-6">
                                        <label className="form-label form-label-custom">Live Preview</label>
                                        <div className="d-flex align-items-center justify-content-center border bg-light" style={{ height: '220px', borderColor: '#cbd5e1' }}>
                                            <div className="text-center px-4">
                                                <div className="mb-3">
                                                    <span
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: 56,
                                                            height: 56,
                                                            borderRadius: '50%',
                                                            background: '#fef2f2',
                                                            border: '1.5px solid #fecaca'
                                                        }}
                                                    >
                                                        <FaGift style={{ color: '#ed3237', fontSize: 24 }} />
                                                    </span>
                                                </div>
                                                <h5 className="fw-bold text-dark mb-1" style={{ fontSize: '1rem' }}>
                                                    {offerData.name || <span className="text-muted fst-italic">Offer Name</span>}
                                                </h5>
                                                <div className="d-flex align-items-center justify-content-center gap-2 mt-2">
                                                    <strong className="text-danger" style={{ fontSize: '1.5rem' }}>
                                                        {offerData.value || '—'}
                                                    </strong>
                                                    {offerData.unit && (
                                                        <span className={`badge px-2 py-1 ${offerData.unit === 'Percent' ? 'bg-success-subtle text-success border border-success-subtle' : 'bg-primary-subtle text-primary border border-primary-subtle'}`} style={{ fontSize: '0.8rem' }}>
                                                            {offerData.unit === 'Percent' ? <FaPercent className="me-1" style={{ fontSize: '10px' }} /> : <FaRupeeSign className="me-1" style={{ fontSize: '10px' }} />}
                                                            {offerData.unit}
                                                        </span>
                                                    )}
                                                </div>
                                                {offerData.minAmount && (
                                                    <small className="text-muted d-block mt-2">
                                                        Min. order: ₹{offerData.minAmount}
                                                    </small>
                                                )}
                                                {!offerData.name && (
                                                    <small className="text-muted d-block mt-2">Fill in the fields to see a preview</small>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .edit-offer-card-custom {
                    margin-left: -1rem !important;
                    margin-right: -1rem !important;
                    margin-bottom: 0 !important;
                    border-left: none !important;
                    border-right: none !important;
                    border-top: none !important;
                    border-bottom: 1px solid #cbd5e1 !important;
                    border-radius: 0 !important;
                }

                .form-label-custom {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
                    font-size: 0.72rem !important;
                    text-transform: uppercase !important;
                    font-weight: 700 !important;
                    color: #64748b !important;
                    letter-spacing: 0.5px !important;
                    margin-bottom: 6px !important;
                    display: block !important;
                }

                .form-control-custom {
                    border: 1px solid #cbd5e1 !important;
                    border-radius: 0 !important;
                    font-size: 0.88rem !important;
                    padding: 8px 12px !important;
                }

                .form-control-custom:focus {
                    border-color: #ed3237 !important;
                    box-shadow: 0 0 0 3px rgba(237, 50, 55, 0.1) !important;
                    outline: none !important;
                }
            `}</style>
        </Fragment>
    );
}

export default EditOfferPage;
